import { useState, useEffect, useCallback } from 'react';
import { Search, FileText, Download, Filter, RussianRuble, Eye, DownloadIcon, ShoppingCart } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { API_URL } from '../config';
import { authFetch } from '../utils/authFetch';


export function DocumentRegistry({ userData }) {
  const [documents, setDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [boughtFilter, setBoughtFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');
  const [modalDoc, setModalDoc] = useState(null);
  const [currentUserId, setCurrentUserId] = useState();

  const UPLOADS_URL = API_URL.replace('/api', '');
  

  const categoriesMap = [
    { id: 'workCult', name: 'Культура работы' },
    { id: 'projManag', name: 'Управление проектами' },
    { id: 'operEff', name: 'Операционная эффективность' },
    { id: 'hr', name: 'HR' },
    { id: 'demandPlann', name: ' Планирование потребности' },
    { id: 'provisPlann', name: 'Планирование обеспечения' },
    { id: 'purch', name: 'Закупки' },
    { id: 'logic', name: 'Логистика' },
    { id: 'money', name: 'Финансы' },
    { id: 'nsi', name: 'Развитие НСИ' },
    { id: 'automat', name: 'Автоматизация' },
    { id: 'strat', name: 'Стратегия' }
  ];

  const boughtStatus = [
    {id: 'false', name: 'Только не купленные '},
    {id: 'true', name: 'Только купленные'},
  ];

  const getCategoryName = (catId) => {
    const entry = categoriesMap.find(c => c.id === catId);
    return entry ? entry.name : catId;
  };

    const refreshAccount = useCallback(async () => {
      try {
        const r = await authFetch(`${API_URL}/me`, { method: "GET" });
        if (!r.ok) return;
        const me = await r.json();
        setCurrentUserId(me.id);
      } catch {}
    }, []);
  
     useEffect(() => {
      refreshAccount();
    }, [refreshAccount]);


const buy = async (doc) => {
  try {
    const res = await authFetch(`${API_URL}/seller/multisplit/payments/initiate`, {
      method: 'POST',
      body: JSON.stringify({ document_id: doc.id }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `Ошибка ${res.status}`);
    }
    const { paymentUrl } = await res.json();
    window.location.href = paymentUrl;
  } catch (err) {
    console.error('Ошибка при покупке:', err);
    alert(err.message);
  }
};

const downloadDocument = async (doc) => {
  try {
    const res = await authFetch(`${API_URL}/documents/${doc.id}/download`, {
      method: 'GET',
    });
    if (!res.ok) throw new Error(res.statusText);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a   = document.createElement('a');
    a.href    = url;
    a.download= doc.original_name || doc.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Ошибка скачивания:', err);
    alert(err.message);
  }
};


  const fetchRegistry = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await authFetch(`${API_URL}/documents/all/authors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Ошибка ${res.status}`);
      const data = await res.json();
      setDocuments(data);
    } catch (err) {
      console.error('Ошибка загрузки реестра:', err);
    }
  };

  useEffect(() => {
    fetchRegistry();
  }, []);

  const location = useLocation();
  
  // повторный fetch, когда приходит bindingResult=success
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('bindingResult') === 'success' || params.get('purchaseResult') === 'success') {
      fetchRegistry();
    }
  }, [location.search]);

  const categories = Array.from(new Set(documents.map(d => d.category)));

  const filtered = documents
    .filter(doc => {
      const term = searchTerm.trim().toLowerCase();
      const matchesSearch = !term || doc.title.toLowerCase().includes(term);
      const matchesCategory = categoryFilter === 'all' || doc.category === categoryFilter;
      const matchesStatus = boughtFilter === 'all' || String(doc.purchased) === boughtFilter;
      return matchesSearch && matchesCategory && matchesStatus;
    })
    .sort((a, b) => {
      if (sortOrder === 'asc') return a.title.localeCompare(b.title);
      return b.title.localeCompare(a.title);
    });

  const totalDocuments = documents.length;
  const averagePrice = totalDocuments
    ? Math.round(documents.reduce((sum, d) => sum + Number(d.price || 0), 0) / totalDocuments)
    : 0;

    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="text-center space-y-2">
        <h1 className="gradient-text text-3xl font-bold">Реестр документов</h1>
        <p className="text-gray-600">Найдите и скачайте одобренные документы</p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-blue-700">Всего документов</span>
          </div>
          <p className="text-2xl text-blue-800 mt-2">{totalDocuments.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
          <div className="flex items-center gap-2">
            <Download className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-700">Скачиваний</span>
          </div>
          <p className="text-2xl text-green-800 mt-2">{documents.reduce((sum, d) => sum + Number(d.sales_count || 0), 0)}</p>
        </div>
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
          <div className="flex items-center gap-2">
            <RussianRuble className="h-5 w-5 text-purple-600" />
            <span className="text-sm text-purple-700">Средняя цена</span>
          </div>
          <p className="text-2xl text-purple-800 mt-2">{averagePrice}₽</p>
        </div>
      </div>



      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Поиск и фильтры
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Поиск документов, авторов, тегов..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Категория" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все категории</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {getCategoryName(category)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={boughtFilter} onValueChange={setBoughtFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Статус" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все</SelectItem>
                {boughtStatus.map((status) => (
                  <SelectItem key={status.id} value={status.id}>
                    {status.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}>Сортировать: {sortOrder === 'asc' ? '↑' : '↓'}</Button>
          </div>
        </CardContent>
      </Card>
          <div className="grid justify-center grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-6">
                {filtered.map(doc => (
          <Card key={doc.id} className="hover:shadow-md transition-shadow w-[280px] h-[500px] flex flex-col">
            <CardHeader className="pb-3 flex justify-between items-center">
              {doc.image_filename ? (
                <img
                  loading="lazy"
                  decoding="async"
                  src={`${UPLOADS_URL}/uploads/${doc.image_filename}`}
                  alt={doc.image_original_name || 'Превью'}
                  className="w-[236px] h-[150px] object-cover mx-auto mt-4 rounded border"
                />
              ) : (
                <div className="w-[236px] h-[150px] bg-gray-200 flex items-center justify-center mx-auto mt-4 rounded border">
                  <FileText className="h-8 w-8 text-gray-400" />
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-3 flex-1 card-item">
              <div className="flex justify-between items-center">
              <p className="text-xs text-[#CF4EE6]">{getCategoryName(doc.category)}</p>
              <p className="text-lg font-bold text-[#06ad50] text-bold">{doc.price} ₽</p>
              </div>
              <CardTitle className="text-lg leading-tight font-bold">{doc.title}</CardTitle>
              <div
                className="text-black-700 text-sm mb-4 editor-preview prose line-clamp-3"
                dangerouslySetInnerHTML={{ __html: doc.description }}
              />
              <div className='flex flex-row sm:justify-between'>
              <div className='sm:text-sm text-gray flex flex-row gap-2'>
              <DownloadIcon />
                <p>
                  {doc.sales_count}
                </p>
              </div>
              <div className='sm:text-sm text-gray flex flex-row gap-2'>
                {formatFileSize(doc.file_size)}
              </div>
            </div>
              <div className="flex gap-2 pt-2">
              {Number(currentUserId) !== Number(doc.author_id) ? (
                <>
                {doc.purchased ? (
                  <Button
                    className="flex-1 bg-gradient-to-r from-[#3466F8] via-[#CF4EE6] to-[#F6275D] text-white"
                    onClick={() => downloadDocument(doc)}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Скачать
                  </Button>
                ) : (
                  <Button
                    className="flex-1 bg-gradient-to-r from-[#3466F8] via-[#CF4EE6] to-[#F6275D] text-white"
                    onClick={() => buy(doc)}
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Купить
                  </Button>
                )}
                </>
              ) : (
                <p className='text-gray'>Это ваш документ</p>
              )}
              
                {/* Кнопка с глазом без flex-1, поэтому её ширина — по содержимому */}
                <Button variant="outline" onClick={() => setModalDoc(doc)}>
                  <Eye className="h-4 w-4" />
                </Button>
              </div>

            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <p className="col-span-full text-center text-gray-500">Документы не найдены</p>}
          </div>

      {modalDoc && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 w-full !mt-0"
          onClick={() => setModalDoc(null)}   // клик по фону — закрыть
        >
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-5xl mx-[20px] p-6 relative h-[70%] flex flex-col"
            onClick={e => e.stopPropagation()} // чтобы клик внутри окна не закрывал
          >
            {/* крестик */}
            <button
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              onClick={() => setModalDoc(null)}
            >
              ✕
            </button>
            <h2 className="text-2xl font-semibold mb-4">{modalDoc.title}</h2>
            <div className='flex flex-row gap-4'>
              {modalDoc.image_filename ? (
                <img
                  loading="lazy"
                  decoding="async"
                  src={`${UPLOADS_URL}/uploads/${modalDoc.image_filename}`}
                  alt={modalDoc.image_original_name || 'Превью'}
                  className="w-[236px] h-[150px] object-cover rounded border mb-2"
                />
              ) : (
                <div className="w-[236px] h-[150px] bg-gray-200 flex items-center justify-center rounded border mb-2">
                  <FileText className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <div className='flex flex-col gap-2'>
                <p className='text-[#CF4EE6]'><strong>Категория:</strong> {getCategoryName(modalDoc.category)}</p>
                <p className='text-black'><strong>Автор:</strong> {modalDoc.author_name}</p>
                <p className='text-[#06ad50]'><strong>Цена:</strong> {modalDoc.price} ₽</p>
                <div className='flex flex-row sm:justify-between'>
                  <div className='sm:text-sm text-gray flex flex-row gap-2'>
                  <DownloadIcon />
                    <p>
                      {modalDoc.sales_count}
                    </p>
                  </div>
                </div>
                <p className='text-gray'>{formatFileSize(modalDoc.file_size)}</p>
              </div>
            </div>
            <div
                  className="prose prose-sm sm:prose-base max-w-none w-full overflow-auto mt-4 flex-1"
                  dangerouslySetInnerHTML={{ __html: modalDoc.description }}
                />
            {/* кнопки скачать/купить прямо из модалки */}
            <div className="mt-auto flex gap-2">
              {Number(currentUserId) !== Number(modalDoc.author_id) ? (
                <>
              {modalDoc.purchased ? (
                <Button
                  onClick={() => {
                    downloadDocument(modalDoc);
                    setModalDoc(null);
                  }}
                  className="flex-1 bg-gradient-to-r from-[#3466F8] via-[#CF4EE6] to-[#F6275D] text-white"
                >
                  <Download className="h-4 w-4 mr-2"/>Скачать
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    buy(modalDoc);
                    setModalDoc(null);
                  }}
                  className="flex-1 bg-gradient-to-r from-[#3466F8] via-[#CF4EE6] to-[#F6275D] text-white"
                >
                  <ShoppingCart />
                  Купить
                </Button>
              )}
              </>
              ) : (
                <p className='text-gray'>Это ваш документ</p>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
